import isFunction from 'lodash-es/isFunction';
import { IBundleResolver } from './resolver';
import { SystemActions } from './SystemActions';
import { SystemDevtool } from './SystemDevtool';
import { SystemInvariant } from './SystemInvariant';
import { SystemLogger } from './util/SystemLogger';
import { SystemSelector } from './SystemSelector';
import { View } from './view';
// tslint:disable:max-file-line-count

declare const VERSION: any;

import {
  ModuleError,
  MissingModule,
  AlreadyParsedBundle,
  SystemAlreadyBooted,
  AlreadyLoadedModule,
  AlreadyFetchedBundle,
  CannotSetBundleExport,
  CircularDependencies,
} from './errors';
import {
  State,
  Action,
  BlobUrl,
  Selector,
  Listener,
  Lifecycle,
  Unsubsrcibe,
  BundleStatus,
  ExportOfModule,
  ExportOfBundle,
  ModuleRequirer,
  ModuleStatus as ModuleStatusEnum,
  BundlesTopology,
  ResolvedBundlesTopology,
} from './types';

declare global {
  // tslint:disable-next-line:interface-name
  interface Window {
    // tslint:disable-next-line
    System?: System<any, any, any, any>;
    VERSION?: any;
  }
}

const ModuleStatus = ModuleStatusEnum;

export class System<
  BundleName extends string,
  ModuleName extends string,
  BundlesModules extends { [K in BundleName]: ModuleName },
  ModulesExport extends { [K in ModuleName]: any }
> {
  static ModuleStatus = ModuleStatusEnum;

  private state: State<BundleName, ModuleName, BundlesModules, ModulesExport>;
  private stateListeners: Array<Listener<ModuleName>> = [];
  private bundleResolver: IBundleResolver<BundleName>;
  private ensureModuleLoadedQueue: Promise<any> = Promise.resolve();
  private invariant = new SystemInvariant<BundleName, ModuleName, BundlesModules, ModulesExport>();
  private selectors = new SystemSelector<BundleName, ModuleName, BundlesModules, ModulesExport>(this.invariant);
  private actions = new SystemActions<BundleName, ModuleName, BundlesModules, ModulesExport>(this.selectors);
  private devtool: SystemDevtool | null;

  constructor(config: {
    bundleResolver: IBundleResolver<BundleName>;
    topology: BundlesTopology<BundleName, ModuleName>;
    preloadModules?: Array<ModuleName>;
  }) {
    const toBind: Array<string> = [
      'select',
      'selectCurrent',
      'createShadowModule',
      'getModule',
      'getModuleOrNull',
      'getModuleIfLoaded',
      'getModuleStatus',
      'getModuleError',
      'hideLoader',
      'fatalError',
      'wait',
      'moduleRequested',
      'listenStateChange',
      'getModuleStatusReason',
      'ensureModuleFetched',
      'ensureMultipleModuleFetched',
      'ensureModuleParsed',
      'ensureMultipleModuleParsed',
      'ensureModuleLoaded',
    ];
    toBind.forEach(func => {
      this[func] = this[func].bind(this);
    });
    const { bundleResolver: bResolver, topology, preloadModules = [] } = config;
    this.state = {
      booted: false,
      bundles: {},
      modules: {},
      loadingModule: null,
      topology: this.resolveTopology(topology),
    };
    try {
      // View display loader and fatal error
      View.init(this.invariant);
      if (this.selectCurrent(this.selectors.isBooted)) {
        throw new SystemAlreadyBooted();
      }
      this.invariant.windowSystemNotDefined();
      // Register System on window to make it accessible everywhere
      window.System = this;
      window.VERSION = VERSION;
      this.dispatch(this.actions.setBooted);
      this.bundleResolver = bResolver;
      const showDevTool = false;
      if (process.env.NODE_ENV === 'development') {
        if (showDevTool) {
          this.devtool = new (require('./SystemDevtool').SystemDevtool as typeof SystemDevtool)(this.invariant);
          if (this.devtool) {
            this.listenStateChange(this.devtool.render);
          }
        }
      }
      const entryModule = this.state.topology.entry;
      Promise.all([this.ensureMultipleModuleParsed(...preloadModules), this.ensureModuleLoaded(entryModule)]).catch(
        error => {
          // if (error instanceof ModuleError) {
          //   const errorModule = error.getModuleName();
          //   if (errorModule === entryModule) {
          //     handleError(error);
          //     return;
          //   }
          // }
          this.handleError(error);
        }
      );
    } catch (error) {
      this.handleError(error);
    }

    window.addEventListener('error', (ev: ErrorEvent) => {
      this.handleError(ev.error);
    });
  }

  /**
   * Used to create shadow modules
   */
  createShadowModule<K extends ModuleName>(module: object, moduleName: K): void {
    const getModule = this.getModule;
    const moduleRequested = this.moduleRequested;
    Object.defineProperty(module, 'exports', {
      configurable: true,
      enumerable: true,
      get(): any {
        // If module is not loaded this throw an error
        // the error is then caught and the missing module is loaded.
        const value = getModule(moduleName);

        // In dev mode we call moduleRequested so system
        // can detect dependencies (needed for hot-reload)
        if (process.env.NODE_ENV === 'development') {
          moduleRequested(moduleName);
        } else {
          // In prod we replace lazy loader with initialized value
          Object.defineProperty(module, 'exports', {
            configurable: false,
            enumerable: true,
            value,
            writable: false,
          });
        }

        return value;
      },
      set: val => {
        if (Object.keys(val).length === 0) {
          return;
        }
        throw new CannotSetBundleExport(moduleName);
      },
    });
  }

  /**
   * When import { ... } from 'module/MyModule'
   * this function is called
   */
  getModule<M extends ModuleName>(moduleName: M): ExportOfModule<ModuleName, ModulesExport, M> {
    if (this.selectCurrent(this.selectors.moduleHasLoaded(moduleName))) {
      return this.selectCurrent(this.selectors.moduleExport(moduleName));
    }
    throw new MissingModule(moduleName);
  }

  getModuleOrNull<M extends ModuleName>(moduleName: M): ExportOfModule<ModuleName, ModulesExport, M> | null {
    if (this.selectCurrent(this.selectors.moduleHasLoaded(moduleName))) {
      return this.selectCurrent(this.selectors.moduleExport(moduleName));
    }
    return null;
  }

  getModuleIfLoaded<M extends ModuleName, Output>(
    moduleName: M,
    ifLoaded: (module: ExportOfModule<ModuleName, ModulesExport, M>) => Output
  ): Output | null {
    if (this.selectCurrent(this.selectors.moduleHasLoaded(moduleName))) {
      return ifLoaded(this.selectCurrent(this.selectors.moduleExport(moduleName)));
    }
    return null;
  }

  getModuleStatus<M extends ModuleName>(moduleName: M): ModuleStatusEnum {
    return this.selectCurrent(this.selectors.moduleStatus(moduleName));
  }

  getModuleError<M extends ModuleName>(moduleName: M): any {
    return this.selectCurrent(this.selectors.moduleError(moduleName));
  }

  hideLoader(): void {
    View.hideLoader();
  }

  fatalError(error: any): void {
    this.handleError(error);
  }

  wait(time?: number): Promise<void> {
    return new Promise((resolve, reject) => {
      window.setTimeout(resolve, time);
    });
  }

  /**
   * Used by internal to determine module dependencies
   */
  moduleRequested(moduleName: ModuleName): void {
    this.dispatch(this.actions.addModuleDependency(moduleName));
  }

  listenStateChange(listener: Listener<ModuleName>): Unsubsrcibe {
    this.stateListeners.push(listener);
    listener(this.selectCurrent(this.selectors.publicSystemState()));
    return () => {
      const index: number = this.stateListeners.indexOf(listener);
      if (index === -1) {
        this.logWarning(`Listener already unsubscribed !`);
        return;
      }
      this.stateListeners.splice(index, 1);
    };
  }

  getModuleStatusReason<M extends ModuleName>(moduleName: M): { [K in ModuleName]?: ModuleStatusEnum } {
    return this.selectCurrent(this.selectors.moduleStatusReason(moduleName));
  }

  // NOTE: This return the blobUrl of the bundle
  ensureModuleFetched<M extends ModuleName>(moduleName: M): Promise<BlobUrl> {
    return this.addToModuleLoadedQueue(() => {
      return this.ensureBundleFetched(this.selectCurrent(this.selectors.bundleOfModule(moduleName)));
    });
  }

  ensureMultipleModuleFetched<M extends ModuleName>(...moduleNames: Array<M>): Promise<{}> {
    return this.addToModuleLoadedQueue(() => {
      return Promise.all(moduleNames.map(moduleName => this.ensureModuleFetched(moduleName)));
    });
  }

  ensureModuleParsed<M extends ModuleName>(moduleName: M): Promise<ModuleRequirer<ModuleName, ModulesExport, M>> {
    return this.addToModuleLoadedQueue(() => {
      return this.ensureBundleParsed(this.selectCurrent(this.selectors.bundleOfModule(moduleName))).then(
        bundleExport => {
          return this.invariant.isFunction(bundleExport.modules[moduleName as string]);
        }
      );
    });
  }

  ensureMultipleModuleParsed<M extends ModuleName>(...moduleNames: Array<M>): Promise<{}> {
    return this.addToModuleLoadedQueue(() => {
      if (moduleNames.length === 0) {
        return Promise.resolve({});
      }
      return Promise.all(moduleNames.map(moduleName => this.ensureModuleParsed(moduleName)));
    });
  }

  ensureModuleLoaded<M extends ModuleName>(moduleName: M): Promise<ExportOfModule<ModuleName, ModulesExport, M>> {
    return this.addToModuleLoadedQueue(() => this.ensureModuleLoadedInternal(moduleName));
  }

  ensureMultipleModuleLoaded<M extends ModuleName>(...moduleNames: Array<M>): Promise<{}> {
    return this.addToModuleLoadedQueue(() => {
      if (moduleNames.length === 0) {
        return Promise.resolve({});
      }
      return Promise.all(moduleNames.map(moduleName => this.ensureModuleLoaded(moduleName)));
    });
  }

  private dispatch(action: Action<BundleName, ModuleName, BundlesModules, ModulesExport>): void {
    const prevState = this.state;
    this.state = action(this.state, this.select);
    const sysState = this.selectCurrent(this.selectors.publicSystemState());
    const prevSysState = this.select(this.selectors.publicSystemState(), prevState);
    if (JSON.stringify(prevSysState) !== JSON.stringify(sysState)) {
      this.stateListeners.forEach(listener => {
        listener(sysState);
      });
    }
  }

  private ensureBundleFetched<B extends BundleName>(bundleName: B): Promise<BlobUrl> {
    const bStatus = this.selectCurrent(this.selectors.bundleStatus(bundleName));
    const bState = this.selectCurrent(this.selectors.bundleState(bundleName));
    switch (bStatus) {
      case BundleStatus.Void:
        return this.fetchBundle(bundleName);
      case BundleStatus.Fetching:
        return this.invariant.notNull(bState.fetching);
      case BundleStatus.Fetched:
      case BundleStatus.Parsing:
      case BundleStatus.Parsed:
        return Promise.resolve(this.invariant.notNull(bState.fetched));
      case BundleStatus.Error:
        return Promise.reject(bState.error);
    }
  }

  private fetchBundle<B extends BundleName>(bundleName: B): Promise<BlobUrl> {
    if (this.selectCurrent(this.selectors.bundleHasFetched(bundleName))) {
      throw new AlreadyFetchedBundle(bundleName);
    }
    SystemLogger.debug(`[BUNDLE][FETCHING] ${bundleName}`);
    const fetchPromise = this.bundleResolver
      .resolve(bundleName)
      .then(bundleUrl => {
        return fetch(bundleUrl)
          .then(response => this.handleCodeResponse(bundleName, response, bundleUrl))
          .then(blob => {
            SystemLogger.debugGroup(
              `[BUNDLE][FETCHED] ${bundleName}`,
              this.selectCurrent(this.selectors.modulesOfBundles(bundleName))
            );
            this.dispatch(this.actions.setBundleFetched(bundleName, blob));
            return blob;
          });
      })
      .catch(err => {
        this.dispatch(this.actions.setBundleError(bundleName, err));
        return Promise.reject(err);
      });
    this.dispatch(this.actions.setBundleFetching(bundleName, fetchPromise));
    return fetchPromise;
  }

  private ensureBundleParsed<B extends BundleName>(
    bundleName: B
  ): Promise<ExportOfBundle<BundleName, ModuleName, BundlesModules, ModulesExport, B>> {
    const bStatus = this.selectCurrent(this.selectors.bundleStatus(bundleName));
    const bState = this.selectCurrent(this.selectors.bundleState(bundleName));
    switch (bStatus) {
      case BundleStatus.Void:
      case BundleStatus.Fetching:
      case BundleStatus.Fetched:
        return this.parseBundle(bundleName);
      case BundleStatus.Parsing:
        return this.invariant.notNull(bState.parsing);
      case BundleStatus.Parsed:
        return Promise.resolve(this.invariant.notNull(bState.parsed));
      case BundleStatus.Error:
        return Promise.reject(bState.error);
    }
  }

  private parseBundle<B extends BundleName>(
    bundleName: B
  ): Promise<ExportOfBundle<BundleName, ModuleName, BundlesModules, ModulesExport, B>> {
    if (this.selectCurrent(this.selectors.bundleHasParsed(bundleName))) {
      throw new AlreadyParsedBundle(bundleName);
    }
    const parsePromise = this.ensureBundleFetched(bundleName).then(blob => {
      SystemLogger.debug(`[BUNDLE][PARSING] ${bundleName}`);
      return this.injectCode(bundleName, blob).then(bundleResult => {
        const bundleExport = this.invariant.parseBundleResultValid(bundleName, bundleResult);
        SystemLogger.debug(`[BUNDLE][PARSED] ${bundleName}`);
        this.dispatch(this.actions.setBundleParsed(bundleName, bundleExport));
        return bundleExport;
      });
    });
    this.dispatch(this.actions.setBundleParsing(bundleName, parsePromise));
    return parsePromise;
  }

  private addToModuleLoadedQueue<R>(item: () => Promise<R>): Promise<R> {
    return new Promise<R>((resolve, reject) => {
      this.ensureModuleLoadedQueue = this.ensureModuleLoadedQueue.then(() => {
        const result = item();
        resolve(result);
      });
    });
  }

  private resolveTopology(
    topology: BundlesTopology<BundleName, ModuleName>
  ): ResolvedBundlesTopology<BundleName, ModuleName> {
    const modules = topology.modules;
    return {
      modules: modules,
      bundles: Object.keys(topology.bundles).reduce<{ [K in BundleName]: Array<ModuleName> }>(
        (acc, bundleName) => {
          acc[bundleName] = topology.bundles[bundleName].map((modIndex: number) => modules[modIndex]);
          return acc;
        },
        // tslint:disable-next-line:no-object-literal-type-assertion
        {} as any
      ),
      dependencies: Object.keys(topology.dependencies).reduce<{ [M in ModuleName]: Array<ModuleName> }>(
        (acc, moduleIndex) => {
          acc[modules[moduleIndex]] = topology.dependencies[moduleIndex].map((modIndex: number) => modules[modIndex]);
          return acc;
        },
        {} as any
      ),
      entry: topology.entry,
    };
  }

  private ensureModuleLoadedInternal<M extends ModuleName>(
    moduleName: M
  ): Promise<ExportOfModule<ModuleName, ModulesExport, M>> {
    const mStatus = this.selectCurrent(this.selectors.moduleStatus(moduleName));
    const mState = this.selectCurrent(this.selectors.moduleState(moduleName));
    switch (mStatus) {
      case ModuleStatus.Void:
      case ModuleStatus.Fetching:
      case ModuleStatus.Fetched:
      case ModuleStatus.Parsing:
      case ModuleStatus.Parsed:
        return this.loadModule(moduleName);
      case ModuleStatus.Loading:
        return this.invariant.notNull(mState.loading);
      case ModuleStatus.Loaded:
        return Promise.resolve(this.invariant.notNull(mState.loaded));
      case ModuleStatus.Error:
        return Promise.reject(mState.error);
    }
  }

  private loadModule<M extends ModuleName>(
    moduleName: M,
    tryAgain: boolean = false
  ): Promise<ExportOfModule<ModuleName, ModulesExport, M>> {
    if (this.selectCurrent(this.selectors.moduleHasLoaded(moduleName))) {
      throw new AlreadyLoadedModule(moduleName);
    }
    const moduleDependencies = this.selectCurrent(this.selectors.dependenciesOfModule(moduleName));
    const loadPromise: Promise<ExportOfModule<ModuleName, ModulesExport, M>> = this.ensureMultipleModuleLoaded(
      ...moduleDependencies
    ).then(() => {
      return this.ensureModuleParsed(moduleName).then(moduleRequirer => {
        SystemLogger.debug(`[MODULE][LOADING] ${moduleName}`);
        let module: ExportOfModule<ModuleName, ModulesExport, M> | null = null;
        let missingModule: ModuleName | false = false;
        let moduleError: MissingModule<ModuleName, any> | undefined;
        try {
          this.dispatch(this.actions.setLoadingModule(moduleName));
          module = moduleRequirer();
          this.dispatch(this.actions.unsetLoadingModule());
        } catch (error) {
          if (error instanceof MissingModule) {
            moduleError = error;
            missingModule = error.getMissingModuleName();
            this.dispatch(this.actions.resetModuleDependencies(moduleName));
          } else {
            this.dispatch(this.actions.setModuleError(moduleName, error));
            throw new ModuleError(moduleName, error);
          }
        }

        // Load dependency, then load the module
        if (missingModule && moduleError) {
          SystemLogger.debug(`[MODULE][MISSING] ${missingModule} in ${moduleName}`);
          // We clear module cache because error make all exports to be undefined.
          this.clearCache(moduleName);
          // If we already know that we need this module, we just wait for it
          if (this.selectCurrent(this.selectors.moduleHasMissingDependency(moduleName, missingModule))) {
            return this.ensureModuleLoaded(missingModule).then(() => {
              return this.ensureModuleLoaded(moduleName);
            });
          }
          // Circular dependency ?
          this.dispatch(
            this.actions.addMissingDependency(
              moduleName,
              this.invariant.notFalse(missingModule),
              this.invariant.notUndefined(moduleError)
            )
          );
          if (this.selectCurrent(this.selectors.moduleHasCircularDependencies(missingModule))) {
            const path = this.selectCurrent(this.selectors.findCircularDependenciesPath(missingModule));
            throw new CircularDependencies(path);
          }
          this.dispatch(this.actions.setModuleLoadingReason(moduleName, missingModule));
          return this.ensureModuleLoaded(missingModule).then(() => {
            this.dispatch(this.actions.setModuleLoadingReason(moduleName, null));
            // try again to load the module
            return this.loadModule(moduleName, true);
          });
        }
        // Module required success call moduleDidLoad
        // Invariant.moduleIsEsModule(module);
        const moduleNotNull = this.invariant.notNull(module);
        SystemLogger.debug(`[MODULE][WILL_LOAD] ${moduleName}`);
        return this.execModuleWillLoad(moduleName, moduleNotNull).then(() => {
          this.dispatch(this.actions.setModuleLoaded(moduleName, moduleNotNull));
          SystemLogger.debug(`[MODULE][LOADED] ${moduleName}`);
          return this.execModuleDidLoad(moduleName, moduleNotNull)
            .then(() => moduleNotNull)
            .then(mod => {
              return mod;
            });
        });
      });
    });
    if (tryAgain === false) {
      // set loadPromise
      this.dispatch(this.actions.setModuleLoading(moduleName, loadPromise));
    }
    return loadPromise;
  }

  private clearCache<M extends ModuleName>(moduleName: M): void {
    const bundleName = this.selectCurrent(this.selectors.bundleOfModule(moduleName));
    const bundleParsed = this.invariant.notNull(this.selectCurrent(this.selectors.bundleState(bundleName)).parsed);
    const cache = bundleParsed.getCache();

    Object.keys(cache).forEach(key => {
      if (cache[key]) {
        delete cache[key];
      }
    });
  }

  private select<O>(
    selector: Selector<BundleName, ModuleName, BundlesModules, ModulesExport, O>,
    overrideState: State<BundleName, ModuleName, BundlesModules, ModulesExport>
  ): O {
    return selector(overrideState ? overrideState : this.state, this.select);
  }

  private selectCurrent<O>(selector: Selector<BundleName, ModuleName, BundlesModules, ModulesExport, O>): O {
    return this.select(selector, this.state);
  }

  private execModuleDidLoad<M extends ModuleName>(
    moduleName: M,
    module: ExportOfModule<ModuleName, ModulesExport, M>
  ): Promise<void> {
    // SystemLogger.debut('moduleDidLoad > ' + moduleName);
    if (isFunction((module as any).moduleDidLoad)) {
      const didLoad: Lifecycle.moduleDidLoad<BundleName, ModuleName, BundlesModules, ModulesExport> = (module as any)
        .moduleDidLoad;
      return Promise.resolve(didLoad(this)).then(() => module as any);
    }
    return Promise.resolve();
  }

  private execModuleWillLoad<M extends ModuleName>(
    moduleName: M,
    module: ExportOfModule<ModuleName, ModulesExport, M>
  ): Promise<void> {
    // SystemLogger.debut('moduleWillLoad > ' + moduleName);
    if (isFunction((module as any).moduleWillLoad)) {
      const willLoad: Lifecycle.moduleWillLoad<BundleName, ModuleName, BundlesModules, ModulesExport> = (module as any)
        .moduleWillLoad;
      return Promise.resolve(willLoad(this)).then(() => module as any);
    }
    return Promise.resolve();
  }

  /**
   * In dev we replace sourceMap url
   * In prod we just return the blob
   */
  private handleCodeResponse<K extends BundleName>(
    bundleName: K,
    response: Response,
    bundleUrl: string
  ): Promise<BlobUrl> {
    return Promise.resolve()
      .then(() => {
        if (process.env.NODE_ENV === 'development') {
          // We can't have both Chrome sourcemap and react-error-overlay source map
          // Chrome wants : //# sourceMappingURL=http://localhost:3000/...
          // react-error-overlay wants : //# sourceMappingURL=572f7872-b9bb-4f94-83c9-b86c17b12a24

          return response.text().then(code => {
            const sourceMapUrl = bundleUrl + '.map';
            const origin = new URL(response.url).origin;
            const fileName = response.url.split('/').slice(-1)[0];
            const correctedCode = code.replace(
              `//# sourceMappingURL=${fileName}.map`,
              `//# sourceMappingURL=${origin + sourceMapUrl}`
            );
            return this.createBlob(correctedCode);
          });

          // Some old code for react-error-overlay, keep this around, just in case...
          // return fetch(sourceMapUrl).then(mapResponse => {
          //   return mapResponse.text().then(mapText => {
          //     // const correctedSourceMap = mapText.replace(`"file":"static/js/${fileName}",`, '');
          //     // const sourcemapBlobName = sourcemapBlobUrl.split('/').slice(-1)[0];
          //     // const sourcemapBlobUrl = URL.createObjectURL(createBlob(mapText));
          //     // const sourcemapBlobName = sourcemapBlobUrl.replace(/^blob:https?:/, '');
          //     return response.text().then(code => {
          //     });
          //   });
          // });
        } else {
          return response.blob();
        }
      })
      .then(blob => {
        return URL.createObjectURL(blob);
      });
  }

  private createBlob(content: string, type: string = 'application/javascript'): Blob {
    let blob;
    try {
      blob = new Blob([content], { type });
    } catch (e) {
      // Backwards-compatibility
      const BlobBuilder =
        (window as any).BlobBuilder || (window as any).WebKitBlobBuilder || (window as any).MozBlobBuilder;
      blob = new BlobBuilder();
      blob.append(content);
      blob = blob.getBlob();
    }
    return blob;
  }

  private injectCode<K extends BundleName>(bundleName: K, blobUrl: BlobUrl): Promise<any> {
    return new Promise((resolve, reject) => {
      const scriptElem = document.createElement('script');
      scriptElem.type = 'text/javascript';
      scriptElem.src = blobUrl;
      scriptElem.onload = () => {
        resolve((window as any).Bundle);
      };
      document.getElementsByTagName('head')[0].appendChild(scriptElem);
    });
  }

  private handleError(error: any): void {
    if (error.getHelp) {
      SystemLogger.info(error.getHelp());
    }
    console.dir(error);
    SystemLogger.error(error);
    View.displayError(error, this.invariant);
  }

  private logWarning(content: Array<string> | string): void {
    const constentArr = Array.isArray(content) ? content : [content];
    SystemLogger.warnGroup(`⚠️ ` + constentArr[0], [constentArr.join('\n')]);
  }
}



// WEBPACK FOOTER //
// ./node_modules/front-system/engine/System.ts